import { roundUsingFormat } from '@lmt/expressions';
import { MeasuredValue } from '@lmt/unicorn-shared/app/features/measurements/types';
import { convertToDateInClinicTimezone } from '@lmt/unicorn-shared/app/helpers/timezone-change-helper';
import { differenceInMilliseconds, parse, startOfDay } from 'date-fns';

function parseLine(line: string): MeasuredValue {
  const values = line.split('\x04');
  if (values.length !== 4 || !values[0].trim()) {
    throw new Error(`Can't parse file. Error in line: ${line}.`);
  }

  const measuredValue: MeasuredValue = {
    key: values[0],
    name: values[1],
    value: values[2],
    unit: values[3],
  };
  return measuredValue;
}

export function parseData(data: string): MeasuredValue[] {
  const lines = data
    .replace(/\r/g, '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '') // remove BOM symbols ('fs.readFile' in fileReading.ts doesn't strip BOM symbols)
    .split(/\n/);
  const measuredValues: MeasuredValue[] = lines
    .filter((line) => line !== '')
    .map((line) => parseLine(line));
  return measuredValues;
}

export function isTimeOnlyUnit(unit: string) {
  return unit?.toLowerCase() === 'min' || unit?.toLowerCase() === 'hh:mm:ss';
}

export function parseDate(value: string): Date {
  if (value.length === 14) return parse(value, 'yyyyMMddHHmmss', new Date());
  if (value.length === 8) {
    const date = parse(value, 'yyyyMMdd', 0);
    return convertToDateInClinicTimezone(date);
  }

  const date = new Date(value);
  if (isNaN(date.getTime())) {
    throw new Error(`Unable to parse date from '${date}'`);
  }
  return date;
}

/*
  Returns number of milliseconds since start of a day
 */
export function parseTime(value: string, unit: string): number {
  if (unit === 'sek') {
    const resultInSeconds = parseInt(value);
    return resultInSeconds * 1000;
  }
  const dateFormat = isTimeOnlyUnit(unit)
    ? value.length < 6
      ? 'HH:mm'
      : 'HH:mm:ss'
    : 'yyyyMMddHHmmss';
  const dateTime = parse(value, dateFormat, new Date());
  return differenceInMilliseconds(dateTime, startOfDay(dateTime));
}
/*
  format: we accept the following values: 'h', 'h.0', 'm', 'm.0', 's', 's.0'; there could be more zeroes.
*/
export function parseDateToNumber(
  value: string,
  unit: string,
  format: string,
): string {
  const time = parseTime(value, unit);
  const timeInSeconds = time / 1000;

  const factor = getFactor(format);
  const numberFormat = '0' + format.substr(1); // numberFormat should be something like '0.0' or '0.00', etc
  return roundUsingFormat((timeInSeconds / factor).toString(), numberFormat);
}

function getFactor(format: string): number {
  switch (format.charAt(0)) {
    case 's':
      return 1;
    case 'm':
      return 60;
    case 'h':
      return 3600;
    default:
      throw new Error(`Unknown format '${format}' for parsing`);
  }
}

export const fixedStringPrefix = 'FIXED:';

export function parseFixedString(value: string): string {
  return value.replace(fixedStringPrefix, '').trim();
}
